// Presentation-layer formatters for the analytics view: they wrap the shared
// `@/lib/format` utilities and add the null/absent guard, so every value is
// either the correctly-formatted real value or the canonical EMPTY_VALUE ("—")
// — never a blank, and never a raw ISO string or bare number.

import { EMPTY_VALUE, formatDuration, meetingStamp, weekdayMonthDay } from '@/lib/format';

function toDate(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** ISO datetime → "Fri, Jul 4 · 2:30 PM", or "—" when absent/invalid. */
export function dateTimeOrDash(iso: string | null | undefined): string {
  const d = toDate(iso);
  return d ? meetingStamp(d) : EMPTY_VALUE;
}

/** ISO date → "Fri, Jul 4", or "—" when absent/invalid. */
export function dateOrDash(iso: string | null | undefined): string {
  const d = toDate(iso);
  return d ? weekdayMonthDay(d) : EMPTY_VALUE;
}

/**
 * Seconds → compact duration ("5m 12s"), or "—" only when the value is truly
 * absent (null/undefined). A real 0 renders as "0s" so zero stays distinct from
 * absent.
 */
export function durationOrDash(seconds: number | null | undefined): string {
  return seconds == null ? EMPTY_VALUE : formatDuration(seconds);
}

/** Non-empty text, or "—". Trims so a whitespace-only value reads as absent. */
export function textOrDash(value: string | null | undefined): string {
  const t = value?.trim();
  return t ? t : EMPTY_VALUE;
}
